//==============================================================================
// Extended version of global state's get-cart-state action
//
// The base version will check for the checkout cart cookie. If it exists, that
// cart will be loaded. Otherwise, it will copy the regular cart to a new cart.
//
// This version will maintain standard functionality unless a specific query
// parameter exists in the URL.
// If the query parameter exists, a new cart will be constructed with products
// from the query string.
// It would be better to return an empty cart and leave cart population to
// external code, but an empty cart will cause checkout to redirect.
// Intercepting and adding contents before the redirect would be messier than
// adding cart construction to this data action.
//
// NOTES:
//   This assumes all products are available in the current catalog. Catalog isn't included in the URL.
//   If we add discounts or catalogs, consider encoding the URL for light-weight protection
//==============================================================================
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext,
    getCatalogId,
} from '@msdyn365-commerce/core';
import { Cart, CartType } from '@msdyn365-commerce/retail-proxy';
import { getCartState } from '@msdyn365-commerce/global-state';
import { copyAsync, readAsync, createCartAsync, addCartLinesAsync, addInvoicesAsync, setInvoiceLinePriceAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';

//==============================================================================
// INTERFACES
//==============================================================================
interface ProductAddRequest {
    recId: number
    quantity: number
}

interface InvoiceAddRequest {
    invoiceId: number
    paymentAmount: number
}

//==============================================================================
// INPUT
//==============================================================================

//==========================================================
// Input class for getCheckoutCart data action.
//==========================================================
export class GetCheckoutCartInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;
    public readonly catalogId: number;

    constructor(apiSettings: ICommerceApiSettings, catalogId?: number) {
        this.apiSettings = apiSettings;
        this.catalogId = catalogId || 0;
    }

    public getCacheKey = () => `CheckoutCart-chanId:${this.apiSettings.channelId}-catId:${this.catalogId}`;
    public getCacheObjectType = () => 'CheckoutCart';
    public dataCacheType = (): CacheType => 'none';
}

//==========================================================
//==========================================================
const createInput = (inputData: ICreateActionContext) => {
    const catalogId = getCatalogId(inputData.requestContext);
    return new GetCheckoutCartInput(inputData.requestContext.apiSettings, catalogId);
};

//==============================================================================
// ACTION
//==============================================================================
enum CartTokenPrefix {
    Auth = 't',
    Anon = 'p'
}

//==========================================================
//==========================================================
export async function getCheckoutCart(input: GetCheckoutCartInput, ctx: IActionContext): Promise<Cart | undefined> {

    const useInvoiceCheckout = !!(ctx.requestContext.query?.invoiceParams);
    // Determine whether to use the standard version, the self-populating version, or the invoice version
    // @TODO/dg: Make the query param case-insensitive
    if(useInvoiceCheckout) {
        return getInvoiceCheckoutCart(input, ctx);
    }

    const useSelfPopulating = !!(ctx.requestContext.query?.cart);

    return useSelfPopulating ? getPopulatedCheckoutCart(input, ctx) : standardGetCheckoutCart(input, ctx);
}

//==========================================================
// Stand-alone Version
//==========================================================
async function getPopulatedCheckoutCart(input: GetCheckoutCartInput, ctx: IActionContext): Promise<Cart | undefined> {

    // Parse the request
    const cartRequest = parseCartRequest(ctx.requestContext.query!.cart);

    // Ensure there's at least a single product in the request
    if (cartRequest.length > 0) {

        // Create an empty checkout cart
        let cart = await createCartAsync({ callerContext: ctx, bypassCache: 'none' }, {CartTypeValue: CartType.Checkout} as Cart);
        // Add items to the cart
        cart = await addCartLinesAsync({ callerContext: ctx}, cart.Id, cartRequest.map(entry => ({
            CatalogId: input.catalogId,
            EntryMethodTypeValue: 3,    // @FIXME/dg: Set this by name (which name??)
            ProductId: entry.recId,
            Quantity: entry.quantity,
        })));

        return cart;
    }
    return undefined;
}

//==========================================================
// Invoice checkout cart version
//==========================================================
async function getInvoiceCheckoutCart(input: GetCheckoutCartInput, ctx: IActionContext): Promise<Cart | undefined> {

    // Parse the request
    const cartRequest = parseInvoiceCartRequest(ctx.requestContext.query!.invoiceParams);

    // Ensure there's at least a single product in the request
    if (cartRequest.length) {

        // Create an empty checkout cart
        let cart = await createCartAsync({ callerContext: ctx, bypassCache: 'none' }, { CartTypeValue: CartType.Checkout } as Cart);

        // Add invoices to cart from query request
        cart = await addInvoicesAsync({ callerContext: ctx}, cart.Id, cartRequest.map(invoice => invoice.invoiceId.toString()));

        // Set amount of payment to process for each invoice cartline
        const updatedInvoiceCart = await updateInvoicePriceLines(cart, cartRequest, ctx);

        return updatedInvoiceCart;
    }
    return undefined;
}


//==========================================================
// Update the invoice price lines for any amounts that are partial payments
//==========================================================
async function updateInvoicePriceLines(cart: Cart, cartRequest: InvoiceAddRequest[], ctx: IActionContext): Promise<Cart> {
    // Creating list to track which invoices are being partially paid
    const invoicesToUpdate = cartRequest;
    cart.CartLines?.forEach(cartLine => {
        const invoicePrice = cartRequest.find(invoice => `${invoice.invoiceId}` === cartLine.InvoiceId)?.paymentAmount;
        if(cartLine && cartLine.LineId && cartLine.InvoiceId) {
            if(cartLine.Price! === invoicePrice!) {
                invoicesToUpdate.filter(inv => inv.invoiceId !== +cartLine.InvoiceId!);
            }
        }
    });

    let currentCart = cart;
    let currentInvoice = invoicesToUpdate.pop();

    // Force sequential handling loop of async functionality to avoid cart update collision errors
    while(!!currentInvoice) {
        const cartLineToUpdate = currentCart.CartLines?.find(cartLine => +cartLine.InvoiceId! === currentInvoice!.invoiceId);
        if(cartLineToUpdate) {
            currentCart = await updateInvoicePriceLine(ctx, currentCart.Id, cartLineToUpdate.LineId!, currentInvoice.paymentAmount, currentCart.Version!);
        }
        currentInvoice = invoicesToUpdate.pop();
    }

    return currentCart;
}

//==========================================================
// Handle the async Microsoft call for a single cart line
//==========================================================
async function updateInvoicePriceLine(
    context: IActionContext,
    cartId: string,
    cartLineId: string,
    price: number,
    cartVersion: number): Promise<Cart> {

    // Cart version required despite optional declaration in Microsoft code.
    // Network response returns error that cartVersion is required for this operation, if version is not passed through.
    const newCart = await setInvoiceLinePriceAsync({ callerContext: context}, cartId, cartLineId, price, cartVersion);

    return newCart;

}

//==========================================================
// Parse requests passed in via query parameter
//
// Format: recId:qty,recId:qty,...
//==========================================================
function parseCartRequest(request: string): ProductAddRequest[] {

    // Split the string into a series of item/quantity pairs and process each one
    return request.split(',').reduce(
        (output: ProductAddRequest[], entry) => {

            // Split item record ID and quantity
            const [recId, qty] = entry.split(':');

        // Add to the list if the rec ID and quantity are valid
            if (+recId && +qty) {
                output.push({
                    recId: +recId,
                    quantity: +qty,
                });
            }
            return output;
        },
        []
    );
}

//==========================================================
// Parse invoices passed in via query parameter
//
// Format: invoiceId+paymentAmount&invoiceId+paymentAmount,...
//==========================================================
function parseInvoiceCartRequest(request: string): InvoiceAddRequest[] {

    // Split the string into a series of invoice/amount pairs and process each one
    return request.split(',').reduce(
        (output: InvoiceAddRequest[], entry) => {

            // Split invoice ID and amount being paid
            const [invoiceId, paymentAmount] = entry.split(':');

            // Add to the list if the invoice ID and amount are valid
            if (+invoiceId && +paymentAmount) {
                output.push({
                    invoiceId: +invoiceId,
                    paymentAmount: +paymentAmount,
                });
            }
            return output;
        },
        []
    );
}

//==========================================================
// LEGACY VERSION: Data action to copy the cart for checkout actions.
//==========================================================
async function standardGetCheckoutCart(input: GetCheckoutCartInput, ctx: IActionContext): Promise<Cart | undefined> {
    const savedCheckoutCartId = ctx.requestContext.cookies.getCheckoutCartCookie();
    const cartCookieParts: string[] = savedCheckoutCartId.split(':');

    let checkoutCartId = null;
    const isAuthenticated = ctx.requestContext.user.isAuthenticated;
    if (
        (isAuthenticated && cartCookieParts[0] === CartTokenPrefix.Auth) ||
        (!isAuthenticated && cartCookieParts[0] === CartTokenPrefix.Anon)
    ) {
        checkoutCartId = cartCookieParts[1];
    }

    let checkoutCart: Cart | undefined;
    if (checkoutCartId) {
        try {
            checkoutCart = await readAsync({ callerContext: ctx, bypassCache: 'none' }, checkoutCartId);
        } catch (error) {
            ctx.telemetry.error('Error getting checkout cart based on saved checkout card id');
            ctx.telemetry.exception(error as Error);
        }
    }

    const cartState = await getCartState(ctx);
    if (cartState && cartState.cart.Id !== undefined) {
        if (checkoutCart && checkoutCart.Version && cartState.cart.Version && checkoutCart.Version > cartState.cart.Version) {
            // @CUSTOM - fix for pickup
            if (checkoutCart.DeliveryMode === 'Pickup-PDC') {
                checkoutCart.ShippingAddress = {};
            }
            return Promise.resolve(checkoutCart);
        }
        return copyAsync({ callerContext: ctx }, cartState.cart.Id, 2)
            .then(cart => {
                ctx.requestContext.cookies.setCheckoutCartCookie(cart, isAuthenticated);
                return cart;
            })
            .catch(error => {
                ctx.telemetry.error('Error copying cart');
                ctx.telemetry.exception(error);
                return undefined;
            });
    }

    return undefined;
}

//==============================================================================
// DEFINITION
//==============================================================================
export const getCheckoutCartDataAction = createObservableDataAction({
    id: '@msdyn365-commerce/global-state/get-checkout-cart',
    action: <IAction<Cart | undefined>>getCheckoutCart,
    input: createInput
});

export default getCheckoutCartDataAction;
